import { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainLayout, JSONLD, Hreflang, BreadcrumbSchema } from './app/components';

// Lazy load route components for code splitting
const DateDifference = lazy(() => import('./app/routes/DateDifference').then(m => ({ default: m.DateDifference })));
const AddSubtractDate = lazy(() => import('./app/routes/AddSubtractDate').then(m => ({ default: m.AddSubtractDate })));
const BusinessDays = lazy(() => import('./app/routes/BusinessDays').then(m => ({ default: m.BusinessDays })));
const Weekday = lazy(() => import('./app/routes/Weekday').then(m => ({ default: m.Weekday })));
const WeekNumber = lazy(() => import('./app/routes/WeekNumber').then(m => ({ default: m.WeekNumber })));
const TimeDuration = lazy(() => import('./app/routes/TimeDuration').then(m => ({ default: m.TimeDuration })));
const About = lazy(() => import('./app/routes/About').then(m => ({ default: m.About })));
const Contact = lazy(() => import('./app/routes/Contact').then(m => ({ default: m.Contact })));
const Privacy = lazy(() => import('./app/routes/Privacy').then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import('./app/routes/Terms').then(m => ({ default: m.Terms })));
const FAQ = lazy(() => import('./app/routes/FAQ').then(m => ({ default: m.FAQ })));
const UseCases = lazy(() => import('./app/routes/UseCases').then(m => ({ default: m.UseCases })));
const OtherTools = lazy(() => import('./app/routes/OtherTools').then(m => ({ default: m.OtherTools })));

// Loading component for lazy loaded routes
function RouteLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );
}

function App() {
  const { i18n, t } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Load saved language from localStorage and sync with i18n
    const savedLang = localStorage.getItem('preferredLanguage');
    if (savedLang) {
      setCurrentLanguage(savedLang);
      // Sync i18n with saved language
      i18n.changeLanguage(savedLang).catch((error) => {
        console.error('Failed to change language:', error);
      });
    } else {
      // Sync state with current i18n language
      setCurrentLanguage(i18n.language);
    }
    
    // Mark as initialized immediately - no artificial delay
    setIsInitialized(true);
  }, [i18n]);

  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  const handleLanguageChange = async (lang: string) => {
    try {
      await i18n.changeLanguage(lang);
      setCurrentLanguage(lang);
      localStorage.setItem('preferredLanguage', lang);
    } catch (error) {
      console.error('Failed to change language:', error);
      // Still update state even if i18n fails
      setCurrentLanguage(lang);
      localStorage.setItem('preferredLanguage', lang);
    }
  };

  // Show minimal loading state only during first render
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Get base URL for canonical and structured data
  const baseUrl = import.meta.env.VITE_BASE_URL || window.location.origin;

  // Breadcrumb for home page
  const homeBreadcrumbs = [
    { name: t('app.title', 'Date Calculator'), url: baseUrl + '/' },
  ];

  // JSON-LD structured data for the home page
  const webApplicationSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: 'Date Calculator',
    url: baseUrl,
    description: t('app.description', 'Free online date calculator with business day calculations, timezone support, and multiple date operations'),
    applicationCategory: 'UtilityApplication',
    operatingSystem: 'All',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    },
    featureList: [
      'Date Difference Calculator',
      'Add/Subtract Dates',
      'Business Days Calculator',
      'Weekday Finder',
      'Week Number Calculator',
      'Time Duration Calculator'
    ],
    inLanguage: [currentLanguage, 'en', 'ja', 'zh-Hans', 'ko', 'es'],
    browserRequirements: 'Requires JavaScript. Compatible with all modern browsers.'
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Date Calculator',
    url: baseUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${baseUrl}/?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  };

  const toolCards = [
    {
      to: '/date-difference',
      title: t('home.dateDifference.title'),
      description: t('home.dateDifference.description'),
      color: 'blue',
      icon: 'M8 7V3m8 4V3M5 11h14M7 21h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2z',
    },
    {
      to: '/add-subtract',
      title: t('home.addSubtract.title'),
      description: t('home.addSubtract.description'),
      color: 'green',
      icon: 'M12 5v14m7-7H5',
    },
    {
      to: '/business-days',
      title: t('home.businessDays.title'),
      description: t('home.businessDays.description'),
      color: 'purple',
      icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
    },
    {
      to: '/weekday',
      title: t('home.weekday.title'),
      description: t('home.weekday.description'),
      color: 'amber',
      icon: 'M8 7V3m8 4V3M5 11h14M9 15h6M7 21h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2z',
    },
    {
      to: '/week-number',
      title: t('home.weekNumber.title'),
      description: t('home.weekNumber.description'),
      color: 'teal',
      icon: 'M8 7V3m8 4V3M5 11h14M9 16h1m4 0h1M7 21h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2z',
    },
    {
      to: '/time-duration',
      title: t('home.timeDuration.title'),
      description: t('home.timeDuration.description'),
      color: 'rose',
      icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    },
  ];

  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
    teal: 'bg-teal-50 text-teal-600',
    rose: 'bg-rose-50 text-rose-600',
  };

  const trustItems = [
    { label: t('home.dateDifference.title'), detail: t('home.dateDifference.description'), icon: 'M13 10V3L4 14h7v7l9-11h-7z', color: 'blue' },
    { label: t('home.businessDays.title'), detail: t('home.businessDays.description'), icon: 'M9 12l2 2 4-4m5-3.5V11a9 9 0 11-18 0V6.5L12 3l8 3.5z', color: 'green' },
    { label: t('home.weekNumber.title'), detail: t('home.weekNumber.description'), icon: 'M12 11c1.657 0 3 1.343 3 3v3H9v-3c0-1.657 1.343-3 3-3zm-5 6h10V9a5 5 0 00-10 0v8z', color: 'purple' },
  ];

  return (
    <>
      <JSONLD data={webApplicationSchema} />
      <JSONLD data={organizationSchema} />
      <Hreflang baseUrl={baseUrl} />
      <BreadcrumbSchema items={homeBreadcrumbs} baseUrl={baseUrl} />
      <Suspense fallback={<RouteLoading />}>
        <Routes>
        <Route
          path="/"
          element={
            <MainLayout
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange}
            >
            <section aria-labelledby="home-title">
              <div className="relative overflow-hidden border-b border-blue-100/70 bg-[linear-gradient(110deg,#ffffff_0%,#f6f9ff_58%,#eef4ff_100%)]">
                <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:px-8 lg:py-20">
                  <header>
                    <h1 id="home-title" className="max-w-3xl text-5xl font-black tracking-tight text-slate-950 sm:text-6xl lg:text-7xl">
                      {t('home.title')}
                    </h1>
                    <p className="mt-6 max-w-2xl text-2xl font-bold leading-snug text-slate-600">
                      {t('home.description')}
                    </p>
                    <div className="mt-10 grid gap-4 sm:grid-cols-3">
                      {trustItems.map((item) => (
                        <div key={item.label} className="flex gap-3">
                          <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${colorClasses[item.color]}`} aria-hidden="true">
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                            </svg>
                          </span>
                          <span>
                            <span className="block text-sm font-black text-slate-950">{item.label}</span>
                            <span className="mt-1 block text-sm leading-6 text-slate-600">{item.detail}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </header>

                  <div className="relative mx-auto w-full max-w-sm sm:max-w-md lg:max-w-lg" aria-hidden="true">
                    <img src="/images/calender-hero.png" alt="" className="w-full object-contain drop-shadow-[0_30px_45px_rgba(37,99,235,0.22)]" />
                  </div>
                </div>
              </div>

              <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
                <section aria-label="Calculator Tools">
                  <div className="mb-8 text-center">
                    <p className="text-sm font-black uppercase text-blue-600">{t('home.howItWorksTitle')}</p>
                    <h2 className="mt-3 text-3xl font-black tracking-tight text-slate-950 sm:text-4xl">
                      {t('home.description')}
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {toolCards.map((card) => (
                      <article key={card.to} className="group min-h-60 rounded-[1.5rem] border border-blue-100 bg-white p-6 shadow-[0_16px_45px_rgba(15,23,42,0.06)] transition hover:-translate-y-1 hover:border-blue-200 hover:shadow-[0_24px_70px_rgba(15,23,42,0.1)]">
                        <Link to={card.to} className="flex h-full flex-col rounded-xl">
                          <span className={`mb-5 flex h-16 w-16 items-center justify-center rounded-2xl ${colorClasses[card.color]}`} aria-hidden="true">
                            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={card.icon} />
                            </svg>
                          </span>
                          <h3 className="text-xl font-black text-slate-950">{card.title}</h3>
                          <p className="mt-3 text-base leading-7 text-slate-600">{card.description}</p>
                          <span className="mt-auto flex justify-end pt-6 text-blue-600" aria-hidden="true">
                            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-blue-100 bg-white transition group-hover:bg-blue-600 group-hover:text-white">
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                              </svg>
                            </span>
                          </span>
                        </Link>
                      </article>
                    ))}
                  </div>
                </section>

                <section aria-label="Guides and Tips" className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
                <article className="content-panel">
                  <h2 id="how-it-works" className="text-2xl font-bold text-gray-900 mb-3">
                    {t('home.howItWorksTitle')}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {t('home.howItWorksDescription')}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>{t('home.howItWorksTip1')}</li>
                    <li>{t('home.howItWorksTip2')}</li>
                    <li>{t('home.howItWorksTip3')}</li>
                    <li>{t('home.howItWorksTip4')}</li>
                    <li>{t('home.howItWorksTip5')}</li>
                  </ul>
                </article>

                <article className="content-panel">
                  <h2 id="practical-tips" className="text-2xl font-bold text-gray-900 mb-3">
                    {t('home.practicalTipsTitle')}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {t('home.practicalTipsDescription')}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>{t('home.practicalTip1')}</li>
                    <li>{t('home.practicalTip2')}</li>
                    <li>{t('home.practicalTip3')}</li>
                    <li>{t('home.practicalTip4')}</li>
                    <li>{t('home.practicalTip5')}</li>
                  </ul>
                </article>
              </section>
              </div>
            </section>
          </MainLayout>
        }
      />
      <Route
        path="/date-difference"
        key="date-difference"
        element={
          <DateDifference
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/add-subtract"
        key="add-subtract"
        element={
          <AddSubtractDate
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/business-days"
        key="business-days"
        element={
          <BusinessDays
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/weekday"
        key="weekday"
        element={
          <Weekday
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/week-number"
        key="week-number"
        element={
          <WeekNumber
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/time-duration"
        key="time-duration"
        element={
          <TimeDuration
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/about"
        key="about"
        element={
          <About
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/contact"
        key="contact"
        element={
          <Contact
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/privacy"
        key="privacy"
        element={
          <Privacy
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/terms"
        key="terms"
        element={
          <Terms
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/faq"
        key="faq"
        element={
          <FAQ
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/use-cases"
        key="use-cases"
        element={
          <UseCases
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route
        path="/other-tools"
        key="other-tools"
        element={
          <OtherTools
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
          />
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
      </Suspense>
    </>
  );
}

export default App;
